import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import { supabase } from '../../lib/supabase';

const UserProfileDropdown = ({ user }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const defaultUser = {
    name: user?.name || 'Admin User',
    email: user?.email || 'admin@kidquest.com',
    role: user?.role || 'Administrator',
    avatar: user?.avatar || null,
  };

  const getInitials = (name) => {
    return name?.split(' ')?.map((n) => n?.[0])?.join('')?.toUpperCase()?.slice(0, 2);
  };

  const menuItems = [
    {
      label: 'Profile Settings',
      icon: 'User',
      onClick: () => {
        setIsOpen(false);
        navigate('/profile-settings');
      },
    },
    {
      label: 'Logout',
      icon: 'LogOut',
      onClick: async () => {
        setIsOpen(false);
        await supabase?.auth?.signOut();
        navigate('/login');
      },
    },
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event?.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="user-profile-trigger"
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e?.key === 'Enter' || e?.key === ' ') {
            e?.preventDefault();
            setIsOpen(!isOpen);
          }
        }}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <div className="user-avatar">
          {defaultUser?.avatar ? (
            <img
              src={defaultUser?.avatar}
              alt={defaultUser?.name}
              className="w-full h-full rounded-full object-cover"
            />
          ) : (
            <span>{getInitials(defaultUser?.name)}</span>
          )}
        </div>
        <div className="flex flex-col items-start">
          <span className="text-sm font-medium text-primary-foreground">
            {defaultUser?.name}
          </span>
          <span className="text-xs text-primary-foreground/80">
            {defaultUser?.role}
          </span>
        </div>
        <Icon
          name={isOpen ? 'ChevronUp' : 'ChevronDown'}
          size={16}
          className="ml-auto text-primary-foreground"
        />
      </div>
      {isOpen && (
        <div className="user-profile-dropdown">
          <div className="px-4 py-3 border-b border-border">
            <p className="text-sm font-medium text-foreground">
              {defaultUser?.name}
            </p>
            <p className="text-xs text-muted-foreground">{defaultUser?.email}</p>
          </div>
          {menuItems?.map((item, index) => (
            <div
              key={index}
              onClick={item?.onClick}
              className="user-profile-dropdown-item"
              role="menuitem"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e?.key === 'Enter' || e?.key === ' ') {
                  e?.preventDefault();
                  item?.onClick();
                }
              }}
            >
              <Icon name={item?.icon} size={16} />
              <span>{item?.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UserProfileDropdown;